// FLV player (SRS HTTP-FLV / WS-FLV) via mpegts.js (init-handoff §4.5). mpegts is loaded as a
// UMD global from /vendor/mpegts.js (see index.html) -> window.mpegts.
import type { PlaybackEndpoint } from "../api/types.js";
import { playWhenReady } from "./hls.js";
import type { PlayerDriver } from "./types.js";

interface MpegtsPlayer {
  attachMediaElement(v: HTMLVideoElement): void;
  load(): void;
  play(): void;
  destroy(): void;
  on(event: string, cb: (...args: unknown[]) => void): void;
}
interface MpegtsGlobal {
  isSupported(): boolean;
  createPlayer(media: Record<string, unknown>, config?: Record<string, unknown>): MpegtsPlayer;
  Events: { ERROR: string };
}
declare global {
  interface Window {
    mpegts?: MpegtsGlobal;
  }
}

export class FlvPlayer implements PlayerDriver {
  private player: MpegtsPlayer | null = null;

  async attach(video: HTMLVideoElement, ep: PlaybackEndpoint): Promise<void> {
    const mpegts = window.mpegts;
    if (!mpegts || !mpegts.isSupported()) throw new Error("mpegts.js not available");
    const player = mpegts.createPlayer(
      { type: "flv", isLive: true, url: ep.url },
      { liveBufferLatencyChasing: true, enableWorker: false },
    );
    this.player = player;
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("FLV load timeout")), 8000);
      player.on(mpegts.Events.ERROR, (...args: unknown[]) => {
        clearTimeout(timer);
        reject(new Error(`FLV error: ${String(args[0])}`));
      });
      player.attachMediaElement(video);
      player.load();
      const onData = () => {
        clearTimeout(timer);
        video.removeEventListener("loadeddata", onData);
        void playWhenReady(video);
        resolve();
      };
      video.addEventListener("loadeddata", onData);
    });
  }

  destroy(): void {
    try {
      this.player?.destroy();
    } catch {
      /* ignore */
    }
    this.player = null;
  }
}
